import React, { useEffect, useRef, useState } from "react";
import "./chatbot.css";
import { link, REACT_APP_CHAT_ENDPOINT } from "../../config";
const CHAT_ENDPOINT = REACT_APP_CHAT_ENDPOINT || "/chat";

// 🔍 Helper to detect if text is likely RTL (Arabic, Hebrew, etc.)
function isRTL(text) {
  const rtlPattern = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/; // Arabic & related Unicode blocks
  return rtlPattern.test(text);
}

// 🌐 Helper to detect if the site's language is Arabic
function isArabicSiteLanguage() {
  try {
    const docLang =
      typeof document !== "undefined"
        ? (document.documentElement.lang || "").toLowerCase()
        : "";
    const docDir =
      typeof document !== "undefined"
        ? (document.documentElement.dir || "").toLowerCase()
        : "";

    // Prioritize document language attribute over navigator language
    if (docLang) {
      return docLang.startsWith("ar");
    }
    
    // If no lang attribute, check dir attribute
    if (docDir === "rtl") {
      return true;
    }
    
    // Only use navigator language as last resort if document has no lang/dir
    const navLang =
      typeof navigator !== "undefined"
        ? (navigator.language || navigator.userLanguage || "").toLowerCase()
        : "";
    
    if (navLang.startsWith("ar")) return true;
  } catch {
    // no-op
  }
  return false;
}

function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi! How can I help you today?",
    },
  ]);

  useEffect(() => {
    const setInitialMessage = () => {
      setMessages((prevMessages) => {
        if (prevMessages.length > 1) {
          return prevMessages;
        }

        const isArabic = isArabicSiteLanguage();
        const newMessage = isArabic
          ? "مرحبًا! كيف يمكنني مساعدتك اليوم؟"
          : "Hi! How can I help you today?";

        if (prevMessages[0]?.content !== newMessage) {
          return [{ role: "assistant", content: newMessage }];
        }

        return prevMessages;
      });
    };

    setInitialMessage();

    const observer = new MutationObserver(setInitialMessage);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang", "dir"],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isOpen, isSending]);

  const handleToggle = () => {
    if (!isOpen) {
      setIsOpen(true);
    } else {
      setIsClosing(true);
      setTimeout(() => {
        setIsOpen(false);
        setIsClosing(false);
      }, 200);
    }
  };

  const handleSend = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isSending) return;

    const nextMessages = [...messages, { role: "user", content: trimmed }];
    setMessages(nextMessages);
    setInputValue("");
    setIsSending(true);

    try {
      const res = await fetch(link + CHAT_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ messages: nextMessages }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || "Request failed");
      }

      const data = await res.json();
      const assistantText =
        data?.content ||
        data?.message?.content ||
        data?.choices?.[0]?.message?.content ||
        data?.reply ||
        (typeof data?.message === "string" ? data.message : null) ||
        "Sorry, I couldn't get a response.";

      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: assistantText },
      ]);
    } catch (e) {
      console.log(e);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry—I'm having trouble right now. Please try again.",
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <button
        className={`chat-widget-button ${isOpen ? "active" : ""}`}
        onClick={handleToggle}
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        {isOpen ? (
          <span style={{ fontSize: 18 }}>✕</span>
        ) : (
          <span style={{ fontSize: 18 }}>Ask AI</span>
        )}
      </button>

      {(isOpen || isClosing) && (
        <div
          className={`chat-widget-panel ${
            isOpen && !isClosing ? "open" : "closing"
          }`}
          role="dialog"
          aria-label="Chat Assistant"
        >
          <div className="chat-header">
            <div className="chat-title">Your AI assistant</div>
            <button
              className="chat-close"
              onClick={handleToggle}
              aria-label="Close chat"
            >
              ✕
            </button>
          </div>

          <div className="chat-messages" ref={scrollRef}>
            {messages.map((m, idx) => {
              const rtl = isRTL(m.content);
              return (
                <div
                  key={idx}
                  className={`chat-message ${m.role}`}
                  dir={rtl ? "rtl" : "ltr"}
                  style={{
                    textAlign: rtl ? "right" : "left",
                  }}
                >
                  {m.role === "assistant" ? (
                    <div
                      className="bubble"
                      dangerouslySetInnerHTML={{ __html: m.content }}
                    />
                  ) : (
                    <div className="bubble">{m.content}</div>
                  )}
                </div>
              );
            })}

            {isSending && (
              <div
                className="chat-message assistant"
                dir={isArabicSiteLanguage() ? "rtl" : "ltr"}
                style={{ textAlign: isArabicSiteLanguage() ? "right" : "left" }}
              >
                <div className="bubble" aria-live="polite">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      aria-hidden="true"
                      style={{ color: "#6b7280" }}
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="9"
                        stroke="currentColor"
                        strokeWidth="3"
                        fill="none"
                        opacity="0.25"
                      />
                      <path
                        d="M21 12a9 9 0 0 0-9-9"
                        stroke="currentColor"
                        strokeWidth="3"
                        fill="none"
                      >
                        <animateTransform
                          attributeName="transform"
                          type="rotate"
                          from="0 12 12"
                          to="360 12 12"
                          dur="0.8s"
                          repeatCount="indefinite"
                        />
                      </path>
                    </svg>
                    <span>
                      {isArabicSiteLanguage()
                        ? "يرجى الانتظار بينما نُحضّر أفضل إجابة ممكنة."
                        : "Please wait while we prepare the best possible response."}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="chat-input">
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isSending ? "Sending..." : "Type your message"}
              disabled={isSending}
              rows={2}
              dir={isRTL(inputValue) ? "rtl" : "ltr"}
              style={{ textAlign: isRTL(inputValue) ? "right" : "left" }}
            />
            <button
              onClick={handleSend}
              disabled={isSending || !inputValue.trim()}
              className="send-btn"
            >
              {isSending ? "..." : "Send"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default ChatWidget;
